import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, AlertCircle, Users, Calendar, TrendingUp, Target, CheckCircle, Play, Pause, Lock, Eye, Snowflake, Flame, Sun, Clock, CalendarClock, CheckCircle2, XOctagon, Globe, Linkedin, MessageCircle, MessageSquare, UserCheck, UserPlus, Phone, PhoneCall, Mail, FileText } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { getCampaignLeadStatusTag } from '../../utils/leadStatusBadge';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { useOmniCampaignLeads } from '../../hooks/useSupabaseLeads';
import { supabase } from '../../lib/supabase';
import StatsCard from '../Dashboard/StatsCard';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';
import OmniStepTimeline from './OmniStepTimeline';
import CombinedCallModal from '../shared/CombinedCallModal';
import WhatsAppThreadModal from '../shared/WhatsAppThreadModal';
import EmailThreadModal from '../shared/EmailThreadModal';
import LinkedInThreadModal from '../shared/LinkedInThreadModal';
import ConvoSummaryModal from '../shared/ConvoSummaryModal';

const OmniChannelCampaignPage: React.FC = () => {
  const tz = useTimezone();
  const navigate = useNavigate();
  const { data: omniChannelLeads, isLoading, error } = useOmniCampaignLeads();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectAllLeads, setSelectAllLeads] = useState(false);
  const [activeBulkAction, setActiveBulkAction] = useState<string | null>(null);
  const [timelineLead, setTimelineLead] = useState<{ lead_id: string; name: string; company: string; campaign_id?: string } | null>(null);
  const [callModalLead, setCallModalLead] = useState<{ lead_id: string; name: string } | null>(null);
  const [waThreadLead, setWaThreadLead] = useState<{ leadId: string; name: string; company: string } | null>(null);
  const [emailModalLead, setEmailModalLead] = useState<{ lead_id: string; name: string } | null>(null);
  const [linkedInThreadLead, setLinkedInThreadLead] = useState<{ leadId: string; name: string; company: string } | null>(null);
  const [summaryLead, setSummaryLead] = useState<{ summary: string; name: string; company: string } | null>(null);
  const itemsPerPage = 10;

  const filteredLeads = useMemo(() => {
    return omniChannelLeads.filter(lead => {
      const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || (lead.lead_status || '').toLowerCase() === statusFilter.toLowerCase();
      const matchesTemperature = temperatureFilter === 'all' ||
                                (lead.lead_temperature || '').trim().toLowerCase().includes(temperatureFilter.toLowerCase());
      return matchesSearch && matchesStatus && matchesTemperature;
    });
  }, [omniChannelLeads, searchTerm, statusFilter, temperatureFilter]);

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLeads = useMemo(() => {
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLeads, startIndex, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds([]);
    setSelectAllLeads(false);
  }, [searchTerm, statusFilter, temperatureFilter]);

  const handleSelectAll = () => {
    const allFilteredIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
    if (selectedLeadIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } else {
      setSelectedLeadIds(allFilteredIds);
      setSelectAllLeads(false);
    }
  };

  const handleSelectAllLeads = () => {
    const allFilteredIds = filteredLeads.map(lead => lead.lead_id).filter(Boolean);
    setSelectedLeadIds(allFilteredIds);
    setSelectAllLeads(true);
  };

  const handleSelectLead = (lead_id: string) => {
    if (!lead_id) return;
    setSelectedLeadIds(prev => {
      if (prev.includes(lead_id)) {
        setSelectAllLeads(false);
        return prev.filter(leadId => leadId !== lead_id);
      } else {
        return [...prev, lead_id];
      }
    });
  };

  const allCurrentPageSelected = useMemo(() => {
    const allPageIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
    return allPageIds.length > 0 && allPageIds.every(id => selectedLeadIds.includes(id));
  }, [paginatedLeads, selectedLeadIds]);

  const showSelectAllBanner = allCurrentPageSelected && !selectAllLeads && filteredLeads.length > paginatedLeads.length;

  const handleBulkStatusChange = async (status: string) => {
    if (selectedLeadIds.length === 0) return;
    setActiveBulkAction(status);
    try {
      const leadsToProcess = selectAllLeads
        ? filteredLeads.map(lead => lead.lead_id).filter(Boolean)
        : selectedLeadIds;

      if (status === 'on hold') {
        await supabase.from('leads_tracking')
          .update({ ovrall_lead_status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'on hold', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'omni');
      } else if (status === 'active') {
        const { data: lcRows } = await supabase.from('leads_campaigns')
          .select('lead_id, campaign_step')
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'omni');
        const step0Ids = (lcRows || []).filter(r => (r.campaign_step || 0) === 0).map(r => r.lead_id);
        const stepGte1Ids = (lcRows || []).filter(r => (r.campaign_step || 0) >= 1).map(r => r.lead_id);
        if (step0Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids)
            .eq('campaign_type', 'omni');
          await supabase.from('leads_tracking')
            .update({ ovrall_lead_status: 'assigned', updated_at: new Date().toISOString() })
            .in('lead_id', step0Ids);
        }
        if (stepGte1Ids.length > 0) {
          await supabase.from('leads_campaigns')
            .update({ status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids)
            .eq('campaign_type', 'omni');
          await supabase.from('leads_tracking')
            .update({ ovrall_lead_status: 'follow up', updated_at: new Date().toISOString() })
            .in('lead_id', stepGte1Ids);
        }
      } else if (status === 'closed') {
        await supabase.from('leads_tracking')
          .update({ ovrall_lead_status: 'closed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        await supabase.from('leads_campaigns')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess)
          .eq('campaign_type', 'omni');
      }

      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } catch (error) {
      console.error('Failed to update lead status:', error);
    } finally {
      setActiveBulkAction(null);
    }
  };

  const kpis = useMemo(() => {
    const num = (v: any) => {
      const n = typeof v === 'number' ? v : parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    };

    let leadsContacted = 0;
    let calls = 0;
    let connectedCalls = 0;
    let emailsSent = 0;
    let emailReplies = 0;
    let waSent = 0;
    let waReplies = 0;
    let linkedinConnected = 0;
    let linkedinSent = 0;
    let linkedinReplies = 0;
    let engaged = 0;
    let booked = 0;

    for (const lead of omniChannelLeads) {
      const leadCalls = num(lead.call_attempts);
      const leadCallsAnswered = num(lead.connected_calls);
      const leadEmailsSent = num(lead.ea_emails_sent);
      const leadEmailReplies = num(lead.ea_replies_received);
      const leadWaSent = num(lead.wa_messages_sent);
      const leadWaReplies = num(lead.wa_replies_received);
      const leadLiSent = num(lead.la_messages_sent);
      const leadLiReplies = num(lead.la_replies_received);

      calls += leadCalls;
      connectedCalls += leadCallsAnswered;
      emailsSent += leadEmailsSent;
      emailReplies += leadEmailReplies;
      waSent += leadWaSent;
      waReplies += leadWaReplies;
      linkedinSent += leadLiSent;
      linkedinReplies += leadLiReplies;
      if (lead.la_cr_accepted === true) linkedinConnected++;

      const startedOutreach =
        leadCalls > 0 || leadEmailsSent > 0 || leadWaSent > 0 || leadLiSent > 0 ||
        lead.la_cr_sent === true;
      if (startedOutreach) leadsContacted++;

      const temp = (lead.lead_temperature || '').toLowerCase();
      if (temp.includes('hot') || temp.includes('warm')) engaged++;

      const status = (lead.lead_status || '').toLowerCase();
      if (status.includes('booked') || status.includes('meeting')) booked++;
    }

    const channelRates: number[] = [];
    if (emailsSent > 0) channelRates.push((emailReplies / emailsSent) * 100);
    if (waSent > 0) channelRates.push((waReplies / waSent) * 100);
    if (linkedinSent > 0) channelRates.push((linkedinReplies / linkedinSent) * 100);
    const replyRate = channelRates.length > 0
      ? Math.round(channelRates.reduce((sum, r) => sum + r, 0) / channelRates.length)
      : 0;

    return {
      leadsContacted, calls, connectedCalls, emailsSent, emailReplies,
      waSent, waReplies, linkedinConnected, linkedinSent, linkedinReplies,
      replyRate, engaged, booked,
    };
  }, [omniChannelLeads]);

  const getTemperatureTag = (temperature: string) => {
    if (!temperature || temperature.trim() === '' || temperature.toLowerCase() === 'n/a') return null;
    const tempLower = temperature.toLowerCase();
    if (tempLower.includes('cold')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
        <Snowflake size={12} />
        Cold
      </span>
    );
    if (tempLower.includes('warm')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
        <Sun size={12} />
        Warm
      </span>
    );
    if (tempLower.includes('hot')) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
        <Flame size={12} className="text-red-500" />
        Hot
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">
        {temperature}
      </span>
    );
  };

  const getNextFollowUpTag = (datetime: string, leadStatus?: string) => {
    if (!datetime || datetime.trim() === '' || ['closed', 'booked'].includes((leadStatus || '').toLowerCase())) return <span className="text-xs text-gray-400">--</span>;
    try {
      const followUpDate = new Date(datetime);
      const now = new Date();
      const diffHours = (followUpDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      let IconComp = CalendarClock;
      let colorClasses = 'bg-[#05E0D1]/8 text-[#0bb5a5] border-[#05E0D1]/15';
      let dotColor = 'bg-[#05E0D1]';
      if (diffHours < 0) {
        IconComp = AlertCircle;
        colorClasses = 'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/20';
        dotColor = 'bg-[#1875FF]';
      } else if (diffHours < 24) {
        IconComp = Clock;
        colorClasses = 'bg-gradient-to-r from-[#1875FF]/8 to-[#05E0D1]/8 text-[#1875FF] border-[#1875FF]/15';
        dotColor = 'bg-[#1875FF]/70';
      }
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border shadow-sm ${colorClasses}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
          <IconComp size={11} />
          <span className="text-xs font-semibold">{formatInTz(followUpDate, 'MMM dd, HH:mm', tz)}</span>
        </div>
      );
    } catch {
      return <span className="text-xs text-gray-400">Invalid</span>;
    }
  };

  const getLeadStatusTag = getCampaignLeadStatusTag;

  const getCampaignStepDisplay = (step: number | null, total: number) => {
    const current = step || 0;
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs font-bold text-gray-900">{current} <span className="text-gray-400 font-medium">of</span> {total}</span>
        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  if (isLoading && omniChannelLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate('/campaigns')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} className="mr-1" />
          Back to Campaigns
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 mb-6 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 xl:grid-cols-[repeat(13,minmax(0,1fr))] gap-2">
        <StatsCard title="Leads Contacted" value={kpis.leadsContacted} icon={Users} color="blue" dense />
        <StatsCard title="Calls" value={kpis.calls} icon={Phone} color="blue" dense />
        <StatsCard title="Connected Calls" value={kpis.connectedCalls} icon={PhoneCall} color="green" dense />
        <StatsCard title="Emails Sent" value={kpis.emailsSent} icon={Mail} color="blue" dense />
        <StatsCard title="Email Replies" value={kpis.emailReplies} icon={MessageSquare} color="green" dense />
        <StatsCard title="WhatsApp Sent" value={kpis.waSent} icon={MessageCircle} color="blue" dense />
        <StatsCard title="WhatsApp Replies" value={kpis.waReplies} icon={MessageCircle} color="green" dense />
        <StatsCard title="LinkedIn Connected" value={kpis.linkedinConnected} icon={UserCheck} color="green" dense />
        <StatsCard title="LinkedIn Sent" value={kpis.linkedinSent} icon={Linkedin} color="blue" dense />
        <StatsCard title="LinkedIn Replies" value={kpis.linkedinReplies} icon={Linkedin} color="green" dense />
        <StatsCard title="Reply Rate" value={`${kpis.replyRate}%`} icon={TrendingUp} color="orange" dense />
        <StatsCard title="Engaged Leads" value={kpis.engaged} icon={Target} color="blue" dense />
        <StatsCard title="Booked Meetings" value={kpis.booked} icon={Calendar} color="green" dense />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input type="text" placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium" />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <SimpleCustomSelect value={statusFilter} onChange={setStatusFilter} options={[{ value: 'all', label: 'All Status' }, { value: 'assigned', label: 'Assigned' }, { value: 'queued', label: 'Queued' }, { value: 'calling', label: 'Calling' }, { value: 'emailing', label: 'Emailing' }, { value: 'messaging', label: 'Messaging' }, { value: 'connection pending', label: 'Connection Pending' }, { value: 'talking', label: 'Talking' }, { value: 'chatting', label: 'Chatting' }, { value: 'booked', label: 'Booked' }, { value: 'follow up', label: 'Follow Up' }, { value: 'on hold', label: 'On Hold' }, { value: 'closed', label: 'Closed' }]} placeholder="All Status" className="w-48" />
              <SimpleCustomSelect value={temperatureFilter} onChange={setTemperatureFilter} options={[{ value: 'all', label: 'All Temperature' }, { value: 'cold', label: 'Cold' }, { value: 'warm', label: 'Warm' }, { value: 'hot', label: 'Hot' }]} placeholder="All Temperature" className="w-48" />
            </div>
          </div>
        </div>
      </div>

      {selectedLeadIds.length > 0 && (
        <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border-2 border-[#1875FF]/40 rounded-xl p-5 shadow-2xl shadow-[#1875FF]/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-lg flex items-center justify-center animate-pulse shadow-md">
                <CheckCircle className="text-[#1875FF]" size={20} />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                  {selectAllLeads ? `All ${filteredLeads.length}` : selectedLeadIds.length} lead{(selectAllLeads ? filteredLeads.length : selectedLeadIds.length) > 1 ? 's' : ''} selected
                </span>
                {showSelectAllBanner && (
                  <button onClick={handleSelectAllLeads} className="text-[#1875FF] font-semibold hover:underline focus:outline-none text-sm text-left">
                    Select all {filteredLeads.length} leads
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkStatusChange('active')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'active' ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Updating...</span></>
                ) : (
                  <><Play size={18} /><span>Activate</span></>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('on hold')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#05E0D1]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'on hold' ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Updating...</span></>
                ) : (
                  <><Pause size={18} /><span>On Hold</span></>
                )}
              </button>
              <button
                onClick={() => handleBulkStatusChange('closed')}
                disabled={activeBulkAction !== null}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {activeBulkAction === 'closed' ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Updating...</span></>
                ) : (
                  <><Lock size={18} /><span>Close</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-center px-2 py-4 w-12">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={paginatedLeads.length > 0 && selectedLeadIds.length === paginatedLeads.map(lead => lead.lead_id).filter(Boolean).length}
                      ref={(el) => {
                        if (el) {
                          const allFilteredIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
                          el.indeterminate = selectedLeadIds.length > 0 && selectedLeadIds.length < allFilteredIds.length;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                    />
                  </div>
                </th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[130px]">Person</th>
                <th className="text-left px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[110px]">Company</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Campaign Step</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[120px]">Next Follow Up</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Temperature</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[100px]">Lead Status</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[80px]">Sequence</th>
                <th className="text-center px-2 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-[160px]">Convo Record</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {paginatedLeads.map((lead) => (
                <tr key={lead.lead_id || lead.id} className="hover:bg-[#1875FF]/5 transition-colors">
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={lead.lead_id ? selectedLeadIds.includes(lead.lead_id) : false}
                        onChange={() => handleSelectLead(lead.lead_id)}
                        className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-4 w-[130px]">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-sm font-medium text-gray-900 break-words whitespace-normal">{lead.dm_full_name || 'N/A'}</span>
                      {lead.dm_linkedin && (
                        <a href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile">
                          <Linkedin size={12} />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 break-words whitespace-normal">{lead.dm_title || ''}</div>
                  </td>
                  <td className="px-2 py-4 w-[110px]">
                    <div className="text-sm font-medium text-gray-900 break-words whitespace-normal mb-2">{lead.company_name || 'N/A'}</div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && (
                        <a href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="Visit Website">
                          <Globe size={14} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors" title="LinkedIn Profile">
                          <Linkedin size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    {getCampaignStepDisplay(lead.campaign_step, lead.total_steps || 30)}
                  </td>
                  <td className="px-2 py-4 text-center w-[120px]">
                    {getNextFollowUpTag(lead.next_follow_up, lead.lead_status)}
                  </td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    {getTemperatureTag(lead.lead_temperature)}
                  </td>
                  <td className="px-2 py-4 text-center w-[100px]">
                    {getLeadStatusTag(lead.lead_status)}
                  </td>
                  <td className="px-2 py-4 text-center w-[80px]">
                    <button
                      onClick={() => setTimelineLead({
                        lead_id: lead.lead_id,
                        name: lead.dm_full_name || 'Unknown',
                        company: lead.company_name || 'Unknown',
                        campaign_id: lead.campaign_id,
                      })}
                      className="inline-flex items-center space-x-1 px-2.5 py-1.5 text-xs font-semibold text-[#1875FF] bg-[#1875FF]/10 border border-[#1875FF]/20 rounded-lg hover:bg-[#1875FF]/20 hover:border-[#1875FF]/30 transition-all"
                    >
                      <Eye size={13} />
                      <span>View</span>
                    </button>
                  </td>
                  <td className="px-2 py-4 text-center w-[160px]">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => setCallModalLead({ lead_id: lead.lead_id, name: lead.dm_full_name || 'Unknown' })}
                        title="Call Recordings & Transcripts"
                        className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                      >
                        <Phone size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                      </button>
                      <button
                        onClick={() => setWaThreadLead({ leadId: lead.lead_id, name: lead.dm_full_name || 'Unknown', company: lead.company_name || '' })}
                        title="WhatsApp Thread"
                        className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                      >
                        <MessageCircle size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                      </button>
                      <button
                        onClick={() => setEmailModalLead({ lead_id: lead.lead_id, name: lead.dm_full_name || 'Unknown' })}
                        title="Email Thread"
                        className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                      >
                        <Mail size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                      </button>
                      <button
                        onClick={() => setLinkedInThreadLead({ leadId: lead.lead_id, name: lead.dm_full_name || 'Unknown', company: lead.company_name || '' })}
                        title="LinkedIn Thread"
                        className="group relative w-7 h-7 rounded-lg border border-[#1875FF]/15 bg-[#1875FF]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#1875FF] hover:to-[#05E0D1] hover:border-transparent hover:shadow-md hover:shadow-[#1875FF]/25 transition-all duration-200"
                      >
                        <Linkedin size={13} className="text-[#1875FF] group-hover:text-white transition-colors duration-200" />
                      </button>
                      <button
                        onClick={() => setSummaryLead({ summary: lead.ovrall_convo_summary || '', name: lead.dm_full_name || 'Unknown', company: lead.company_name || '' })}
                        title="Conversation Summary"
                        className="group relative w-7 h-7 rounded-lg border border-[#05E0D1]/20 bg-[#05E0D1]/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#05E0D1] hover:to-[#1875FF] hover:border-transparent hover:shadow-md hover:shadow-[#05E0D1]/25 transition-all duration-200"
                      >
                        <FileText size={13} className="text-[#0bb5a5] group-hover:text-white transition-colors duration-200" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLeads.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-bold">No omni-channel leads found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">{searchTerm || statusFilter !== 'all' || temperatureFilter !== 'all' ? 'Try adjusting your filters' : 'Omni-channel leads will appear here once they\'re added to the system'}</div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredLeads.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {timelineLead && (
        <OmniStepTimeline
          leadId={timelineLead.lead_id}
          leadName={timelineLead.name}
          companyName={timelineLead.company}
          campaignId={timelineLead.campaign_id}
          onClose={() => setTimelineLead(null)}
        />
      )}

      {callModalLead && (
        <CombinedCallModal
          isOpen={true}
          onClose={() => setCallModalLead(null)}
          leadId={callModalLead.lead_id}
          leadName={callModalLead.name}
        />
      )}

      <WhatsAppThreadModal
        isOpen={!!waThreadLead}
        onClose={() => setWaThreadLead(null)}
        leadId={waThreadLead?.leadId || ''}
        leadName={waThreadLead?.name}
        companyName={waThreadLead?.company}
      />

      {emailModalLead && (
        <EmailThreadModal
          isOpen={true}
          onClose={() => setEmailModalLead(null)}
          leadId={emailModalLead.lead_id}
          leadName={emailModalLead.name}
        />
      )}

      <LinkedInThreadModal
        isOpen={!!linkedInThreadLead}
        onClose={() => setLinkedInThreadLead(null)}
        leadId={linkedInThreadLead?.leadId || ''}
        leadName={linkedInThreadLead?.name}
        companyName={linkedInThreadLead?.company}
      />

      <ConvoSummaryModal
        isOpen={!!summaryLead}
        onClose={() => setSummaryLead(null)}
        summary={summaryLead?.summary || ''}
        leadName={summaryLead?.name}
        companyName={summaryLead?.company}
      />
    </div>
  );
};

export default OmniChannelCampaignPage;
